import type { DiagramShapeId } from '@canvas/shared-types';
import { svgDataUrl } from '@/lib/svgDataUrl';

const BG = '#edf1f6';
const INK = '#152033';
const MUTED = '#5a6a7e';

function board(inner: string, w = 160, h = 120): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
  <rect width="${w}" height="${h}" fill="${BG}"/>
  <defs>
    <pattern id="g" width="16" height="16" patternUnits="userSpaceOnUse">
      <path d="M16 0H0V16" fill="none" stroke="#d7dee8" stroke-width="0.6"/>
    </pattern>
  </defs>
  <rect width="${w}" height="${h}" fill="url(#g)" opacity="0.55"/>
  ${inner}
</svg>`;
}

function label(
  text: string,
  x: number,
  y: number,
  fill = '#fff',
  size = 11
): string {
  return `<text x="${x}" y="${y}" text-anchor="middle" dominant-baseline="middle" font-family="Segoe UI, Arial, sans-serif" font-size="${size}" font-weight="600" fill="${fill}">${text}</text>`;
}

/** Basic shape / frame tile thumbnails */
export const SHAPE_PREVIEW_SVGS: Record<string, string> = {
  rect: svgDataUrl(
    board(
      `<rect x="28" y="28" width="104" height="64" rx="4" fill="#0d7377" stroke="${INK}" stroke-width="2"/>`
    )
  ),
  roundedRect: svgDataUrl(
    board(
      `<rect x="26" y="30" width="108" height="60" rx="16" fill="#1b4d89" stroke="${INK}" stroke-width="2"/>`
    )
  ),
  circle: svgDataUrl(
    board(
      `<circle cx="80" cy="60" r="36" fill="#b54708" stroke="${INK}" stroke-width="2"/>`
    )
  ),
  triangle: svgDataUrl(
    board(
      `<polygon points="80,22 122,98 38,98" fill="#45566e" stroke="${INK}" stroke-width="2"/>`
    )
  ),
  line: svgDataUrl(
    board(
      `<line x1="24" y1="60" x2="136" y2="60" stroke="${INK}" stroke-width="5" stroke-linecap="round"/>`
    )
  ),
  star: svgDataUrl(
    board(
      `<polygon points="80,18 92,52 128,52 99,74 110,108 80,86 50,108 61,74 32,52 68,52" fill="#c9a227" stroke="${INK}" stroke-width="1.5"/>`
    )
  ),
  hexagon: svgDataUrl(
    board(
      `<polygon points="80,18 122,39 122,81 80,102 38,81 38,39" fill="#0d7377" stroke="${INK}" stroke-width="2"/>`
    )
  ),
  octagon: svgDataUrl(
    board(
      `<polygon points="56,22 104,22 132,50 132,70 104,98 56,98 28,70 28,50" fill="#5b6abf" stroke="${INK}" stroke-width="2"/>`
    )
  ),
  'frame-circle': svgDataUrl(
    board(`
      <circle cx="80" cy="60" r="38" fill="#dfe6ef" stroke="#7b8aa0" stroke-width="2.5"/>
      <circle cx="80" cy="52" r="12" fill="#b7c3d1"/>
      <path d="M52 88c6-14 50-14 56 0" fill="#b7c3d1"/>
    `)
  ),
  'frame-rounded': svgDataUrl(
    board(`
      <rect x="26" y="24" width="108" height="72" rx="10" fill="#eef2f6" stroke="#9aabbf" stroke-width="2"/>
      <rect x="38" y="36" width="84" height="36" rx="4" fill="#c5d0dc"/>
      <rect x="38" y="78" width="48" height="6" rx="2" fill="#aebccd"/>
    `)
  ),
};

const DIAGRAM_INNER: Record<DiagramShapeId, string> = {
  process: `
    <rect x="28" y="34" width="104" height="52" rx="6" fill="#0d7377" stroke="${INK}" stroke-width="2"/>
    ${label('Process', 80, 60)}`,
  decision: `
    <polygon points="80,18 132,60 80,102 28,60" fill="#e5c07b" stroke="${INK}" stroke-width="2"/>
    ${label('?', 80, 62, INK, 16)}`,
  terminator: `
    <rect x="30" y="42" width="100" height="36" rx="18" fill="#1b4d89" stroke="${INK}" stroke-width="2"/>
    ${label('Start', 80, 60)}`,
  data: `
    <polygon points="48,30 136,30 112,90 24,90" fill="#5b6abf" stroke="${INK}" stroke-width="2"/>
    ${label('Data', 80, 62)}`,
  document: `
    <path d="M34 26h92v58c-18 12-28-8-46 0s-28-12-46 0V26z" fill="#e8eef4" stroke="${INK}" stroke-width="2"/>
    ${label('Doc', 80, 58, INK)}`,
  'manual-input': `
    <polygon points="40,34 132,22 132,90 28,90" fill="#b54708" stroke="${INK}" stroke-width="2"/>
    ${label('Input', 82, 62)}`,
  delay: `
    <path d="M36 30h70a28 28 0 0 1 0 60H36z" fill="#6b8f71" stroke="${INK}" stroke-width="2"/>
    ${label('Delay', 78, 60)}`,
  display: `
    <path d="M48 30h72l20 30-20 30H48L28 60z" fill="#0d7377" stroke="${INK}" stroke-width="2"/>
    ${label('Out', 84, 60)}`,
  predefined: `
    <rect x="28" y="34" width="104" height="52" rx="4" fill="#45566e" stroke="${INK}" stroke-width="2"/>
    <line x1="40" y1="34" x2="40" y2="86" stroke="${INK}" stroke-width="2"/>
    <line x1="120" y1="34" x2="120" y2="86" stroke="${INK}" stroke-width="2"/>
    ${label('Sub', 80, 60)}`,
  connector: `
    <circle cx="80" cy="60" r="28" fill="#e5c07b" stroke="${INK}" stroke-width="2"/>
    ${label('A', 80, 61, INK, 16)}`,
  'stored-data': `
    <path d="M44 28h76c10 0 10 64 0 64H44c-10 0-10-64 0-64z" fill="#1b4d89" stroke="${INK}" stroke-width="2"/>
    <path d="M44 28c10 0 10 64 0 64" fill="none" stroke="${INK}" stroke-width="2"/>
    ${label('DB', 86, 60)}`,
  merge: `
    <polygon points="80,24 128,96 32,96" fill="#0d7377" stroke="${INK}" stroke-width="2"/>
    ${label('∨', 80, 72, '#fff', 14)}`,
  'off-page': `
    <path d="M48 22h64v58l-32 22-32-22z" fill="#5b6abf" stroke="${INK}" stroke-width="2"/>
    ${label('Off', 80, 52)}`,
  actor: `
    <circle cx="80" cy="32" r="12" fill="none" stroke="${INK}" stroke-width="2.5"/>
    <line x1="80" y1="44" x2="80" y2="78" stroke="${INK}" stroke-width="2.5"/>
    <line x1="56" y1="58" x2="104" y2="58" stroke="${INK}" stroke-width="2.5"/>
    <line x1="80" y1="78" x2="58" y2="102" stroke="${INK}" stroke-width="2.5"/>
    <line x1="80" y1="78" x2="102" y2="102" stroke="${INK}" stroke-width="2.5"/>
    ${label('Actor', 80, 112, MUTED, 10)}`,
  usecase: `
    <ellipse cx="80" cy="58" rx="52" ry="28" fill="#e8eef4" stroke="${INK}" stroke-width="2"/>
    ${label('Use case', 80, 59, INK, 10)}`,
  class: `
    <rect x="34" y="18" width="92" height="84" fill="#f7f9fc" stroke="${INK}" stroke-width="2"/>
    <line x1="34" y1="42" x2="126" y2="42" stroke="${INK}" stroke-width="1.5"/>
    <line x1="34" y1="70" x2="126" y2="70" stroke="${INK}" stroke-width="1.5"/>
    ${label('Class', 80, 31, INK, 11)}
    <text x="42" y="56" font-family="Segoe UI, Arial" font-size="9" fill="${MUTED}">+ attr</text>
    <text x="42" y="84" font-family="Segoe UI, Arial" font-size="9" fill="${MUTED}">+ method()</text>`,
  package: `
    <rect x="34" y="28" width="44" height="16" fill="#e5c07b" stroke="${INK}" stroke-width="1.5"/>
    <rect x="34" y="44" width="92" height="56" fill="#f0d78c" stroke="${INK}" stroke-width="2"/>
    ${label('Package', 80, 74, INK, 10)}`,
  note: `
    <path d="M40 24h66l20 20v52H40z" fill="#fff3bf" stroke="${INK}" stroke-width="2"/>
    <path d="M106 24v20h20" fill="none" stroke="${INK}" stroke-width="2"/>
    ${label('Note', 78, 68, INK)}`,
  component: `
    <rect x="40" y="30" width="88" height="60" rx="4" fill="#0d7377" stroke="${INK}" stroke-width="2"/>
    <rect x="32" y="42" width="18" height="12" fill="#0d7377" stroke="${INK}" stroke-width="1.5"/>
    <rect x="32" y="66" width="18" height="12" fill="#0d7377" stroke="${INK}" stroke-width="1.5"/>
    ${label('Comp', 88, 60)}`,
  'arrow-right': `
    <polygon points="24,40 100,40 100,28 140,60 100,92 100,80 24,80" fill="#0d7377" stroke="${INK}" stroke-width="1.5"/>`,
  'arrow-left': `
    <polygon points="136,40 60,40 60,28 20,60 60,92 60,80 136,80" fill="#1b4d89" stroke="${INK}" stroke-width="1.5"/>`,
  'arrow-both': `
    <polygon points="20,60 52,28 52,44 108,44 108,28 140,60 108,92 108,76 52,76 52,92" fill="#5b6abf" stroke="${INK}" stroke-width="1.5"/>`,
  chevron: `
    <polygon points="24,28 108,28 140,60 108,92 24,92 56,60" fill="#e5c07b" stroke="${INK}" stroke-width="1.5"/>`,
  'arrow-bent': `
    <path d="M36 28v52h72" fill="none" stroke="${INK}" stroke-width="5" stroke-linejoin="round" stroke-linecap="round"/>
    <polygon points="108,68 140,80 108,92" fill="${INK}"/>`,
  'arrow-callout': `
    <polygon points="24,36 96,36 96,24 136,60 96,96 96,84 24,84 40,60" fill="#b54708" stroke="${INK}" stroke-width="1.5"/>
    ${label('Hi', 68, 60)}`,
};

export function diagramPreviewUrl(id: DiagramShapeId): string {
  return svgDataUrl(board(DIAGRAM_INNER[id]));
}
